// src/pages/RegisterPage.tsx
import React, { useRef, useState } from 'react';
import { Card, Form, Input, Button, DatePicker, InputNumber, message, Typography, Row, Col, Checkbox } from 'antd';
import { UserOutlined, MailOutlined, CalendarOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../utils/api';
import { format } from 'date-fns';
import { PhoneInput } from '../components/PhoneInput';
import { getPhoneDigitCount, PHONE_REQUIRED_MESSAGE, PHONE_VALIDATION_MESSAGE } from '../utils/phoneUtils';
import { calculateDueDateFromWeek, calculateWeekFromDueDate } from '../utils/pregnancyDates';
import { joinFullName } from '../utils/fullName';
import { Dayjs } from 'dayjs';

const { Title, Text } = Typography;

const PRIMARY_GREEN = '#7CB342';
const BUTTON_DIM_STYLE = {
  backgroundColor: PRIMARY_GREEN,
  borderColor: PRIMARY_GREEN,
  opacity: 0.45,
};
const BUTTON_ACTIVE_STYLE = {
  backgroundColor: PRIMARY_GREEN,
  borderColor: PRIMARY_GREEN,
  opacity: 1,
};

export const RegisterPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const syncLockRef = useRef<'week' | 'due' | null>(null);
  const acceptTerms = Form.useWatch('accept_terms', form);

  const handleWeekChange = (week: number | null) => {
    if (week == null || syncLockRef.current === 'due') {
      return;
    }
    syncLockRef.current = 'week';
    form.setFieldValue('due_date', calculateDueDateFromWeek(week));
    syncLockRef.current = null;
  };

  const handleDueDateChange = (value: Dayjs | null) => {
    if (!value || syncLockRef.current === 'week') {
      return;
    }
    syncLockRef.current = 'due';
    form.setFieldValue('week_of_pregnancy', calculateWeekFromDueDate(value));
    syncLockRef.current = null;
  };

  const onFinish = async (values: any) => {
    try {
      setLoading(true);

      const dueDateFormatted = values.due_date ? format(values.due_date.toDate(), 'yyyy-MM-dd') : null;

      const registerData = {
        email: String(values.email || '').trim().toLowerCase(),
        phone: values.phone,
        password: values.password,
        full_name: joinFullName({
          lastName: values.last_name,
          firstName: values.first_name,
          middleName: values.middle_name,
        }),
        week_of_pregnancy: values.week_of_pregnancy,
        due_date: dueDateFormatted,
        role: 'patient',
      };

      await authAPI.registerPatient(registerData);

      message.success(
        'Регистрация успешна! Проверьте почту и подтвердите email, затем войдите в аккаунт.'
      );
      navigate('/login', { replace: true });
    } catch (error: any) {
      console.error('Registration error:', error);
      message.error(error.response?.data?.detail || 'Ошибка регистрации. Пожалуйста, попробуйте снова.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '80vh',
        backgroundColor: '#F0F9F4',
        padding: '20px',
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 500,
          borderRadius: '12px',
          boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
        }}
        title={
          <div style={{ textAlign: 'center' }}>
            <Title level={2} style={{ color: '#263238', marginBottom: 0 }}>
              Начать заботиться о себе
            </Title>
            <Text type="secondary">Создайте свой личный кабинет</Text>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          initialValues={{
            week_of_pregnancy: 1,
            due_date: calculateDueDateFromWeek(1),
            accept_terms: false,
          }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Пожалуйста, введите email' },
                  { type: 'email', message: 'Введите корректный email' },
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="your.email@example.com" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Телефон"
                name="phone"
                rules={[
                  { required: true, message: PHONE_REQUIRED_MESSAGE },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const count = getPhoneDigitCount(value);
                      if (count !== 10) return Promise.reject(new Error(PHONE_VALIDATION_MESSAGE));
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <PhoneInput placeholder="912 345 67 89" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Фамилия"
                name="last_name"
                rules={[{ required: true, message: 'Пожалуйста, введите фамилию' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="Иванова" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Имя"
                name="first_name"
                rules={[{ required: true, message: 'Пожалуйста, введите имя' }]}
              >
                <Input placeholder="Анна" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Отчество (если есть)" name="middle_name">
                <Input placeholder="Сергеевна" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Срок беременности (неделя)"
                name="week_of_pregnancy"
                rules={[
                  { required: true, message: 'Пожалуйста, укажите срок беременности' },
                  { type: 'number', min: 1, max: 42, message: 'Срок должен быть от 1 до 42 недель' },
                ]}
              >
                <InputNumber
                  placeholder="Неделя"
                  min={1}
                  max={42}
                  style={{ width: '100%' }}
                  size="large"
                  onChange={handleWeekChange}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="ПДР"
                name="due_date"
                rules={[{ required: true, message: 'Пожалуйста, укажите предполагаемую дату родов' }]}
                extra="Можно указать неделю или ПДР — второе поле пересчитается автоматически"
              >
                <DatePicker
                  placeholder="Выберите дату"
                  style={{ width: '100%' }}
                  size="large"
                  suffixIcon={<CalendarOutlined />}
                  format="DD.MM.YYYY"
                  onChange={handleDueDateChange}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Пароль"
            name="password"
            rules={[
              { required: true, message: 'Пожалуйста, введите пароль' },
              { min: 6, message: 'Пароль должен содержать не менее 6 символов' },
            ]}
          >
            <Input.Password placeholder="Введите пароль" size="large" />
          </Form.Item>

          <Form.Item
            name="accept_terms"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error('Примите условия для продолжения')),
              },
            ]}
          >
            <Checkbox>
              Я принимаю условия{' '}
              <Link
                to="/privacy-policy"
                onClick={(e) => e.stopPropagation()}
                style={{
                  color: '#558B2F',
                  fontWeight: 700,
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                }}
              >
                Политики конфиденциальности
              </Link>
            </Checkbox>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              disabled={!acceptTerms}
              style={{
                width: '100%',
                height: '48px',
                fontSize: '16px',
                fontWeight: '500',
                ...(acceptTerms ? BUTTON_ACTIVE_STYLE : BUTTON_DIM_STYLE),
              }}
            >
              Начать заботиться о себе
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Text type="secondary">
            Уже есть аккаунт? <Link to="/login">Войти</Link>
          </Text>
        </div>
      </Card>
    </div>
  );
};
